'use client';

import { ReactNode, useEffect, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { BlogPost } from '../../types/blog';
import { useLocalizedPath } from '../../hooks/useLocalizedPath';
import Breadcrumb from '../ui/Breadcrumb';
import { BlogShareButtons } from './BlogShareButtons';
import { BlogOtherPosts } from './BlogOtherPosts';
import { BlogFAQSection } from './BlogFAQSection';
import { PreferredSourceBlock } from '../PreferredSource/PreferredSourceBlock';
import { PreferredSourcePlacement } from '../../types/preferredSource';
import fullLogoBlack from '../../assets/images/full-logo-black.svg';
import { useTranslations } from 'next-intl';
import { SITE_CONFIG } from '@/constants';
import styles from './BlogPostContent.module.css';

interface BlogPostClientProps {
  post: BlogPost;
  sidebar?: ReactNode;
}

const formatRelativeDate = (date: Date): string => {
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) return 'today';
  if (diffInDays === 1) return 'yesterday';
  if (diffInDays < 7) return `${diffInDays} days ago`;
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
  if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`;
  return `${Math.floor(diffInDays / 365)} years ago`;
};

export function BlogPostContent({ post, sidebar }: BlogPostClientProps) {
  const t = useTranslations('blog');
  const tCommon = useTranslations('common');
  const { localizeFullPath } = useLocalizedPath();

  const publishedDate = post.publishedAt ? new Date(post.publishedAt) : new Date(post.createdAt);
  const formattedDate = formatRelativeDate(publishedDate);
  const readTime = Math.ceil(post.content.split(' ').length / 200);

  // View-count beacon: the post read is edge-cached, so the increment no longer
  // rides along with the SSR fetch. Fire once on mount; bots that don't run JS
  // are excluded, which is the intent.
  const viewBeaconFiredRef = useRef<string | null>(null);
  useEffect(() => {
    if (!post.slug || viewBeaconFiredRef.current === post.slug) return;
    viewBeaconFiredRef.current = post.slug;
    fetch('/api/view', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'blog', id: post.slug }),
      keepalive: true,
    }).catch(() => {});
  }, [post.slug]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.breadcrumbBar}>
        <div className={styles.container}>
          <Breadcrumb items={[
            { label: tCommon('breadcrumb.home'), path: '/' },
            { label: tCommon('breadcrumb.blog'), path: '/blog' },
            { label: post.title, path: `/blog/${post.slug}` }
          ]} />
        </div>
      </div>
      
      <div className={styles.container}>
        <article className={styles.article} data-side-cta-anchor>
          <div className={styles.imageWrapper}>
            <Image
              src={post.featuredImage || fullLogoBlack}
              alt={post.featuredImageAlt || post.title}
              fill
              style={{ objectFit: 'contain' }}
              priority
              unoptimized
            />
          </div>

          <div className={styles.content}>
            <header className={styles.header}>
              {post.tags && post.tags.length > 0 && (
                <div className={styles.tags}>
                  {post.tags.map((tag) => (
                    <span key={tag} className={styles.tag}>{tag}</span>
                  ))}
                </div>
              )}

              <h1 className={styles.title}>{post.title}</h1>

              {post.excerpt && (
                <p className={styles.excerpt}>{post.excerpt}</p>
              )}

              <div className={styles.meta}>
                {post.authorProfile ? (
                  <Link
                    href={localizeFullPath(`/author/${post.authorProfile.slug}`)}
                    className={styles.authorLink}
                  >
                    {t('post.by')} {post.authorProfile.name}
                  </Link>
                ) : (
                  <span>{t('post.by')} {SITE_CONFIG.name}</span>
                )}
                <span>•</span>
                <time dateTime={publishedDate.toISOString()}>{formattedDate}</time>
                <span>•</span>
                <span>{readTime} {t('post.minRead')}</span>
              </div>
            </header>

            <div
              className={styles.body}
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            {post.faq && post.faq.length > 0 && (
              <BlogFAQSection faq={post.faq} />
            )}

            <footer className={styles.footer}>
              <BlogShareButtons title={post.title} slug={post.slug} />
              <PreferredSourceBlock placement={PreferredSourcePlacement.BLOG_POST} className={styles.preferredSource} />
            </footer>
          </div>
        </article>

        <BlogOtherPosts currentPostId={post._id} />
      </div>

      {sidebar}
    </div>
  );
}
